import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Image as ImageIcon } from "lucide-react";

interface PropertyDocument {
  id: string;
  document_name: string;
  document_type: string | null;
  file_url: string;
  file_size: number | null;
  created_at: string;
}

interface Props {
  propertyId: string;
}

export const PropertyDocuments: React.FC<Props> = ({ propertyId }) => {
  const [floorPlans, setFloorPlans] = useState<PropertyDocument[]>([]);
  const [brochures, setBrochures] = useState<PropertyDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewDoc, setPreviewDoc] = useState<PropertyDocument | null>(null);

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("property_documents")
          .select("id, document_name, document_type, file_url, file_size, created_at")
          .eq("property_id", propertyId)
          .order("created_at", { ascending: false });
        if (error) throw error;

        const docs: PropertyDocument[] = data || [];
        setFloorPlans(docs.filter((d) => d.document_type === "floor_plan"));
        setBrochures(docs.filter((d) => d.document_type === "brochure"));
      } catch (e) {
        console.error("Error loading documents", e);
      } finally {
        setLoading(false);
      }
    };

    fetchDocs();
  }, [propertyId]);

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return "Unknown";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const isImageUrl = (url: string) => /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url);

  const DocItem: React.FC<{ doc: PropertyDocument }> = ({ doc }) => {
    const isImage = isImageUrl(doc.file_url);
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
        <div className="shrink-0">
          {isImage ? (
            <ImageIcon className="h-5 w-5 text-primary" />
          ) : (
            <FileText className="h-5 w-5 text-primary" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium truncate" title={doc.document_name}>{doc.document_name}</p>
            {doc.document_type && (
              <Badge variant="secondary" className="text-xs capitalize">
                {doc.document_type.replace("_", " ")}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{formatFileSize(doc.file_size)}</p>
        </div>
        {doc.file_url && (
          <Button size="sm" variant="outline" onClick={() => setPreviewDoc(doc)}>
            Preview
          </Button>
        )}
      </div>
    );
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading documents…</p>;
  }

  if (floorPlans.length === 0 && brochures.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {floorPlans.length > 0 && (
        <section aria-label="Floor Plans">
          <div className="grid gap-3">
            {floorPlans.map((doc) => (
              <DocItem key={doc.id} doc={doc} />
            ))}
          </div>
        </section>
      )}

      {brochures.length > 0 && (
        <section aria-labelledby="brochures-heading">
          <h3 id="brochures-heading" className="text-xl font-semibold mb-3">Brochures</h3>
          <div className="grid gap-3">
            {brochures.map((doc) => (
              <DocItem key={doc.id} doc={doc} />
            ))}
          </div>
        </section>
      )}

      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl w-[90vw]">
          <DialogHeader>
            <DialogTitle>{previewDoc?.document_name}</DialogTitle>
          </DialogHeader>
          {previewDoc?.file_url && (
            <div className="aspect-video w-full select-none" onContextMenu={(e) => e.preventDefault()}>
              {isImageUrl(previewDoc.file_url) ? (
                <img
                  src={previewDoc.file_url}
                  alt={previewDoc.document_name}
                  className="w-full h-full object-contain pointer-events-none"
                  draggable={false}
                />
              ) : (
                <iframe
                  src={`${previewDoc.file_url}#toolbar=0&navpanes=0`}
                  className="w-full h-full rounded-md"
                  title={previewDoc.document_name}
                  sandbox="allow-scripts allow-same-origin"
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};